import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  VideoLibrary as VideoIcon,
  List as ListIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { trailsService } from '../services/trailsService';
import { Trail } from '../types';

const Devotionals: React.FC = () => {
  const [trails, setTrails] = useState<Trail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchTrails();
  }, []);

  const fetchTrails = async () => {
    try {
      const data = await trailsService.getAll();
      // Ordena por ordem
      const sortedData = data.sort((a, b) => (a.order || 0) - (b.order || 0));
      setTrails(sortedData);
    } catch (err: any) {
      setError('Erro ao carregar trilhas');
      // Dados mockados para demonstração
      setTrails([
        {
          id: '1',
          title: 'Bíblia em 1 Ano',
          description: 'Leia toda a Bíblia em um ano com devocionais diários que guiam você através das Escrituras.',
          thumbnailUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=250&fit=crop',
          order: 1,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          devotionals: [],
        },
        {
          id: '2',
          title: 'Fundamentos da Fé',
          description: 'Uma jornada pelos princípios básicos da fé cristã para fortalecer suas bases espirituais.',
          thumbnailUrl: 'https://images.unsplash.com/photo-1504052434569-70ad5836ab65?w=400&h=250&fit=crop',
          order: 2,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          devotionals: [],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta trilha?')) {
      try {
        await trailsService.delete(id);
        fetchTrails();
      } catch (err) {
        setError('Erro ao excluir trilha');
      }
    }
  };

  const handleViewTrail = (trailId: string) => {
    navigate(`/trails/${trailId}/devotionals`);
  };

  // Move trilha para cima ou para baixo
  const handleMoveTrail = async (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= trails.length) return;

    const currentTrail = trails[index];
    const targetTrail = trails[newIndex];

    // Usa índices +1 como base para garantir valores únicos
    const currentNewOrder = newIndex + 1;
    const targetNewOrder = index + 1;

    try {
      await Promise.all([
        trailsService.update(currentTrail.id, { order: currentNewOrder }),
        trailsService.update(targetTrail.id, { order: targetNewOrder }),
      ]);
      fetchTrails();
    } catch (err) {
      setError('Erro ao reordenar trilha');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          mb: 4,
          pb: 3,
          borderBottom: '2px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
            Trilhas de Devocionais
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Organize e gerencie trilhas de conteúdo devocional
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/trails/new')}
          sx={{
            background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #43A047 0%, #5CB860 100%)',
              boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
            },
          }}
        >
          Nova Trilha
        </Button>
      </Box>

      {error && (
        <Alert
          severity="info"
          sx={{
            mb: 3,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #2196F308 0%, #2196F318 100%)',
            border: '1px solid #2196F330',
          }}
        >
          {error} (exibindo dados de demonstração)
        </Alert>
      )}

      {trails.length === 0 ? (
        <Card
          sx={{
            p: 6,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #4CAF5008 0%, #4CAF5018 100%)',
            border: '2px dashed #4CAF5040',
          }}
        >
          <VideoIcon sx={{ fontSize: 64, color: '#4CAF50', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Nenhuma trilha cadastrada
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Comece criando sua primeira trilha de devocionais
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/trails/new')}
            sx={{
              background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
            }}
          >
            Criar Primeira Trilha
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {trails.map((trail, index) => (
            <Grid item xs={12} sm={6} md={4} key={trail.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                  },
                }}
                onClick={() => handleViewTrail(trail.id)}
              >
                <CardMedia
                  component="div"
                  sx={{
                    height: 180,
                    background: trail.thumbnailUrl
                      ? `url(${trail.thumbnailUrl})`
                      : 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {!trail.thumbnailUrl && (
                    <VideoIcon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.8)' }} />
                  )}
                  {/* Botões de reordenar */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      display: 'flex',
                      flexDirection: 'column',
                      background: 'rgba(255,255,255,0.9)',
                      borderRadius: 1,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveTrail(index, 'up');
                      }}
                      disabled={index === 0}
                      sx={{ p: 0.5 }}
                      title="Mover para cima"
                    >
                      <ArrowUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveTrail(index, 'down');
                      }}
                      disabled={index === trails.length - 1}
                      sx={{ p: 0.5 }}
                      title="Mover para baixo"
                    >
                      <ArrowDownIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      display: 'flex',
                      gap: 0.5,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/trails/${trail.id}/devotionals`);
                      }}
                      sx={{
                        background: 'rgba(255,255,255,0.9)',
                        '&:hover': { background: 'rgba(255,255,255,1)' },
                      }}
                      title="Gerenciar Devocionais"
                    >
                      <ListIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/trails/${trail.id}/edit`);
                      }}
                      sx={{
                        background: 'rgba(255,255,255,0.9)',
                        '&:hover': { background: 'rgba(255,255,255,1)' },
                      }}
                      title="Editar Trilha"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(trail.id);
                      }}
                      sx={{
                        background: 'rgba(255,255,255,0.9)',
                        color: 'error.main',
                        '&:hover': { background: 'rgba(255,255,255,1)' },
                      }}
                      title="Excluir Trilha"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {trail.devotionals && trail.devotionals.length > 0 && (
                    <Chip
                      label={`${trail.devotionals.length} vídeos`}
                      size="small"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: 8,
                        background: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  )}
                  {trail.isPremium && (
                    <Chip
                      icon={<StarIcon sx={{ fontSize: 14, color: '#FFF !important' }} />}
                      label="Premium"
                      size="small"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        background: 'linear-gradient(135deg, #FFB300 0%, #FF8F00 100%)',
                        color: 'white',
                        fontWeight: 700,
                        '& .MuiChip-icon': { color: '#FFF' },
                      }}
                    />
                  )}
                </CardMedia>
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography
                    variant="h6"
                    component="h2"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      mb: 1,
                    }}
                  >
                    {trail.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.6,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {trail.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default Devotionals;
